/**
 * Storage — purge orphans modal — per docs/specs/storage.md §6.
 *
 * Owner-only. Dry-run by default: shows orphan count and size to be
 * freed, requires a reason textarea before executing the cleanup.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v3
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { purgeStorageOrphans, useStorageOrphans } from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';

export function StoragePurgeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [reason, setReason] = useState('');
    const [purging, setPurging] = useState(false);
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const qc = useQueryClient();
    const { toast } = useToast();
    const orphans = useStorageOrphans();
    const summary = orphans.data;
    const needsPurge = summary ? summary.objects > 0 : false;
    const isConfirmed = confirmPhrase.trim().toUpperCase() === 'DELETE';

    useEffect(() => {
        if (!open) {
            setReason('');
            setConfirmPhrase('');
        }
    }, [open]);

    const handleClose = () => {
        if (purging) return;
        setReason('');
        setConfirmPhrase('');
        onClose();
    };

    const handlePurge = async () => {
        if (!needsPurge || !isConfirmed) return;

        setPurging(true);
        try {
            const result = await purgeStorageOrphans(reason.trim());
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'storage', 'orphans'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'storage', 'r2'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'storage', 'kpis'] })
            ]);
            toast('Orphan purge finished', {
                kind: result.deleted > 0 ? 'info' : 'success',
                detail: `Deleted ${result.deleted} objects · ${result.deleted_size_gb.toFixed(2)} GB freed`
            });
            handleClose();
        } catch (error) {
            toast('Purge failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Orphan purge could not be completed')
            });
        } finally {
            setPurging(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Purge orphaned R2 objects"
            subtitle="Dry-run preview. Destructive — requires a reason and confirmation phrase."
            width={520}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleClose} disabled={purging}>
                        Cancel
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={handlePurge}
                        disabled={
                            purging ||
                            !reason.trim() ||
                            !summary ||
                            summary.objects === 0 ||
                            !isConfirmed
                        }
                    >
                        {purging ? 'Purging…' : 'Purge now'}
                    </RoleButton>
                </>
            }
        >
            <div
                style={{
                    padding: 12,
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 13,
                    color: 'var(--text-primary)',
                    marginBottom: 12
                }}
            >
                {summary ? (
                    <>
                        <strong>{summary.objects.toLocaleString()}</strong> orphan objects found,
                        will free ~<strong>{summary.size_gb.toFixed(1)} GB</strong>.
                    </>
                ) : (
                    'Loading orphan scan preview…'
                )}
            </div>

            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                Objects with no owning <code className="mono">track_groups</code> row are removed.
                This runs a scoped <code className="mono">cleanup-orphaned-images</code> job and
                cannot be undone.
            </p>

            <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="purge-reason">
                    Reason (required)
                </label>
                <textarea
                    id="purge-reason"
                    className="form-textarea"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. migration cleanup after bucket rename"
                />
                <div className="form-hint">Logged on the admin audit trail.</div>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="purge-confirm">
                    Type DELETE to confirm
                </label>
                <input
                    id="purge-confirm"
                    className="form-input"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder="DELETE"
                />
                <div className="form-hint">
                    This destructive action is capped at 100 deletions per run and logged with your
                    reason.
                </div>
            </div>
        </Modal>
    );
}
